import { normalizeRelayUrl, isPublicRelayUrl } from "@/utils/url"
import { KINDS, tagValue, type DraftEvent, type NostrEvent, type NostrFilter } from "../types"

export interface RelayReview {
  event: NostrEvent
  relayUrl: string
  stars: number
  content: string
  pubkey: string
  createdAt: number
}

/**
 * Relay reviews are kind 31987 addressable events with `d` = relay URL and
 * `rating` = stars / 5 (Jumble's relay review convention).
 */
export function parseRelayReview(event: NostrEvent): RelayReview | undefined {
  if (event.kind !== KINDS.RELAY_REVIEW) return undefined
  const raw = tagValue(event, "d")
  const relayUrl = normalizeRelayUrl(raw)
  if (!relayUrl || !isPublicRelayUrl(relayUrl)) return undefined
  const rating = Number.parseFloat(tagValue(event, "rating") ?? "")
  const stars = Number.isFinite(rating) ? Math.min(5, Math.max(0, rating * 5)) : 0
  return {
    event,
    relayUrl,
    stars,
    content: event.content,
    pubkey: event.pubkey,
    createdAt: event.created_at,
  }
}

export function relayReviewFilter(relayUrl?: string, limit = 40): NostrFilter {
  const filter: NostrFilter = { kinds: [KINDS.RELAY_REVIEW], limit }
  if (relayUrl) {
    const normalized = normalizeRelayUrl(relayUrl)
    if (normalized) filter["#d"] = [normalized]
  }
  return filter
}

export function reviewStats(reviews: RelayReview[]): { average: number; count: number } {
  const rated = reviews.filter((review) => review.stars > 0)
  if (rated.length === 0) return { average: 0, count: reviews.length }
  const total = rated.reduce((sum, review) => sum + review.stars, 0)
  return { average: Math.round((total / rated.length) * 10) / 10, count: reviews.length }
}

export function buildRelayReviewDraft(relayUrl: string, content: string, stars: number): DraftEvent {
  const normalized = normalizeRelayUrl(relayUrl)
  if (!normalized) throw new Error(`invalid relay URL: ${relayUrl}`)
  const rating = Math.min(5, Math.max(0, stars)) / 5
  return {
    kind: KINDS.RELAY_REVIEW,
    content: content.trim(),
    tags: [
      ["d", normalized],
      ["rating", rating.toString()],
    ],
    created_at: Math.floor(Date.now() / 1000),
  }
}
